import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Avatar from '@liquidweb-dev/undercurrent/Avatar';
import ButtonBase from '@liquidweb-dev/undercurrent/ButtonBase';
import Link from '@liquidweb-dev/undercurrent/Link';
import ListItemIcon from '@liquidweb-dev/undercurrent/ListItemIcon';
import Menu from '@liquidweb-dev/undercurrent/Menu';
import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import Badge from '@liquidweb-dev/undercurrent/Badge';
import ListItemText from '@liquidweb-dev/undercurrent/ListItemText';
import LogOutIcon from '@liquidweb-dev/undercurrent/icons/lucide/LogOut';
import useLogout from 'modules/oidc/useLogout';
import useAuthenticated from 'modules/oidc/useAuthenticated';
import referAFriendDialogActions from 'modules/referAFriendDialog/actions';
import { selectors as appConfigSelectors } from 'modules/appConfig';
import useContactList from 'modules/queries/contact/useList';
import useSecureDataShareList from 'modules/queries/secureDataShare/useList';
import { IntroTarget, introTargets } from 'containers/introjs';
import Tooltip from '@liquidweb-dev/undercurrent/Tooltip';
import useLeftHandLayout from '../context';
import HookedLink from './HookedLink';

export function TopNavUser() {
	const [menuElement, setMenuElement] = useState();
	const open = menuElement !== undefined;
	const isAuthed = useAuthenticated();

	const { data: contactListData } = useContactList(
		{},
		{ enabled: isAuthed, retry: true },
	);
	const defaultContact = contactListData?.items.find(
		(contact) => contact.type === 'main',
	);
	const nameInitial = (defaultContact?.fname ||
		defaultContact?.lname ||
		'?')[0];

	const { data: secureDataShares } = useSecureDataShareList(undefined, {
		enabled: isAuthed,
		refetchInterval: 30000,
	});

	const dispatch = useDispatch();
	const logout = useLogout();

	const { appPath } = useLeftHandLayout();

	// Close menu when appPath changes
	useEffect(() => {
		if (appPath === undefined) return;
		setMenuElement(undefined);
	}, [appPath]);

	const userProfilePath = useSelector(appConfigSelectors.getUserProfilePath);
	const referAFriend = () => {
		setMenuElement(undefined);
		dispatch(referAFriendDialogActions.toggleDialog(true));
	};

	const hasSecureNotes = secureDataShares?.item_total > 0;
	const showBadge = hasSecureNotes;

	return (
		<>
			<Tooltip title="User Menu">
				<IntroTarget introId={introTargets.navAccountMenu}>
					<ButtonBase
						onClick={({ currentTarget }) => setMenuElement(currentTarget)}
						sx={(theme) => ({
							borderRadius: theme.borderRadius.roundedFull,
							'&:focus-visible': {
								outlineOffset: '2px',
								outlineWidth: '2px',
								outlineStyle: 'solid',
								outlineColor: theme.palette.uc.highlight.border.main,
							},
						})}
					>
						<Badge
							color={showBadge ? 'error' : undefined}
							overlap="circular"
							variant="dot"
						>
							<Avatar>{nameInitial}</Avatar>
						</Badge>
					</ButtonBase>
				</IntroTarget>
			</Tooltip>
			<Menu
				open={open}
				anchorEl={menuElement}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				onClose={() => setMenuElement()}
				sx={{ zIndex: { xs: 1201, sm: 1200 } }}
			>
				<MenuItem component={HookedLink} href="/account" role="menuitem">
					<ListItemText primary="Account overview" />
				</MenuItem>
				<MenuItem
					component={Link}
					href={userProfilePath}
					target="_blank"
					role="menuitem"
				>
					<ListItemText primary="My user profile" />
				</MenuItem>
				<MenuItem component={HookedLink} href="/account/users" role="menuitem">
					<ListItemText primary="Users" />
				</MenuItem>
				<MenuItem
					component={HookedLink}
					href="/account/history"
					role="menuitem"
				>
					<ListItemText primary="History" />
				</MenuItem>
				<MenuItem
					component={HookedLink}
					href="/account/secure-notes"
					role="menuitem"
				>
					<ListItemText>Secure notes</ListItemText>
					{hasSecureNotes && <Badge color="error" variant="dot" />}
				</MenuItem>
				<MenuItem
					component={HookedLink}
					href="/account/policies"
					role="menuitem"
				>
					<ListItemText primary="Policies" />
				</MenuItem>
				<MenuItem onClick={referAFriend}>
					<ListItemText primary="Refer a friend" />
				</MenuItem>
				<MenuItem onClick={logout} color="danger">
					<ListItemIcon>
						<LogOutIcon sx={{ height: '16px', width: '16px' }} />
					</ListItemIcon>
					<ListItemText primary="Logout" />
				</MenuItem>
			</Menu>
		</>
	);
}

export default TopNavUser;
